import axios from 'axios';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { catchError } from '../Modules/BaseModule/Components/Error';

class ApiResult {
	data: any;
	httpStatus: number;
}

// @ts-ignore
const meddiAppGlobal = MeddiApp;

const axiosConfig = {
	baseURL: meddiAppGlobal.socketUrl,
	timeout: 30000,
	headers: meddiAppGlobal.jsHeaders,
};

const cleanUrlString = (url: string) => {
	return url
		.replaceAll(
			/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/g,
			'<uuid>',
		)
		.split('?')[0]
		.replace(/(.+)\/$/, '$1');
};

const userApi = axios.create(axiosConfig);
userApi.interceptors.response.use(
	response => {
		meddiAppGlobal.statsd.increment('rest.request', 1, {
			endpoint: cleanUrlString(response.config.url || '/'),
			method: response.config.method,
			statusCode: response.status,
		});
		return response;
	},
	function (error) {
		return Promise.reject(error);
	},
);

const apolloCache = new InMemoryCache();
const apolloClient = new ApolloClient({
	uri:
		meddiAppGlobal.socketUrl +
		(meddiAppGlobal.socketUrl.slice(-1) === '/' ? '' : '/') +
		'graphql',
	cache: apolloCache,
	headers: meddiAppGlobal.jsHeaders,
});

export const postMultipart = async (url, data) => {
	return post(url, data, { 'Content-Type': 'multipart/form-data' });
};

export interface IResponse {
	data: unknown;
	status?: number;
	statusText?: string;
}

export const post = async (url, data, config = {}) => {
	let result;
	try {
		result = await userApi.post(url, data, config);
	} catch (e: any) {
		console.log({ e });
		result = catchError(e);
	}

	return {
		data: result.data,
		status: result.status,
		statusText: result.statusText,
	};
};

export const put = async (url, data, config = {}) => {
	let result;
	try {
		result = await userApi.put(url, data, config);
	} catch (e: any) {
		result = catchError(e);
	}

	return {
		data: result.data,
		status: result.status,
		statusText: result.statusText,
	};
};

export const putMultipart = async (url, data) => {
	return put(url, data, { 'Content-Type': 'multipart/form-data' });
};

export const patch = async (url, data) => {
	let result;
	try {
		result = await userApi.patch(url, data, {});
	} catch (e: any) {
		result = catchError(e);
	}

	return {
		data: result.data,
		status: result.status,
		statusText: result.statusText,
	};
};

export const remove = async url => {
	let result;
	try {
		result = await userApi.delete(url, {});
	} catch (e: any) {
		result = catchError(e);
	}

	return {
		data: result.data,
		status: result.status,
		statusText: result.statusText,
	};
};

export const get = async (url, config = {}) => {
	let result;
	try {
		result = await userApi.get(url, config);
	} catch (e: any) {
		result = catchError(e);
	}

	return {
		data: result.data,
		status: result.status,
		statusText: result.statusText,
	};
};

export const graphql = async query => {
	const result = await apolloClient.query(query);
	const response = {
		data: result.data,
	};
	// @ts-ignore
	response.meta = result.meta || null;
	return response;
};

export const graphqlMutate = async query => {
	return await apolloClient.mutate(query);
};

// @ts-ignore
export const clear = async field => {
	await apolloClient.refetchQueries({
		updateCache(cache) {
			cache.evict({ fieldName: field });
		},
	});
};

export default {
	get,
	post,
	postMultipart,
	put,
	putMultipart,
	patch,
	delete: remove,
	graphql,
	graphqlMutate,
	clear,
};
